/**
 * MODALE: SendEmailModal
 * Modale pour envoyer un email avec éditeur de texte riche
 */

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { sendEmailSchema, type SendEmailInput } from '@/schemas/email';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, RichTextEditor } from '../ui';
import { EmailTemplateSelector } from '../email/EmailTemplateSelector';
import { useState } from 'react';
import { X } from 'lucide-react';
import type { EntityType } from '@/types/timeline';
import type { EmailTemplate } from '@/types/emailTemplate';
import { getTagsByEntityType, isTemplateVisibleFor } from '@/lib/emailTags';
import { useSupabase } from '@/hooks/useSupabase';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { toast } from 'sonner';

// V4 R8 — corps RichTextEditor "vide" = soit string vide, soit balisage <p></p>
// résiduel laissé par l'éditeur après reset. Trim + retrait du markup minimal.
function isBodyEmpty(body: string): boolean {
  if (!body) return true;
  const stripped = body.replace(/<p>\s*<\/p>/g, '').trim();
  return stripped.length === 0;
}

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  defaultRecipients?: string[];
  onSuccess?: () => void;
}

export function SendEmailModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  defaultRecipients = [],
  onSuccess,
}: SendEmailModalProps) {
  const supabase = useSupabase();
  const { templates: allTemplates } = useEmailTemplates();
  const emailTemplates = allTemplates.filter((t) =>
    isTemplateVisibleFor(t.entity_scope, entityType as 'contact' | 'crew' | 'prospect')
  );
  const form = useForm<SendEmailInput>({
    resolver: zodResolver(sendEmailSchema),
    defaultValues: {
      to: defaultRecipients,
      cc: [],
      subject: '',
      body: '',
    },
  });

  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const toEmails = form.watch('to') || [];
  const ccEmails = form.watch('cc') || [];

  const handleClose = () => {
    form.reset({ to: [], cc: [], subject: '', body: '' });
    setToInput('');
    setCcInput('');
    setSelectedTemplateId('');
    onClose();
  };

  // V4 R8 — appliquer un modèle. Si l'utilisateur a déjà tapé du contenu (sujet
  // OU corps), confirmer avant d'écraser. "Aucun" remet juste le state à vide
  // sans toucher au form (l'utilisateur garde son brouillon).
  const handleApplyTemplate = (template: EmailTemplate | null) => {
    if (!template) {
      setSelectedTemplateId('');
      return;
    }
    const currentSubject = form.getValues('subject') || '';
    const currentBody = form.getValues('body') || '';
    const hasUserContent = currentSubject.trim().length > 0 || !isBodyEmpty(currentBody);
    if (hasUserContent) {
      const ok = window.confirm(
        'Appliquer ce modèle remplacera le sujet et le corps actuels. Continuer ?'
      );
      if (!ok) return;
    }
    setSelectedTemplateId(template.id);
    form.setValue('subject', template.subject, { shouldValidate: true, shouldDirty: true });
    form.setValue('body', template.body, { shouldValidate: true, shouldDirty: true });
  };

  const addEmail = (type: 'to' | 'cc', email: string) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) return;

    const currentEmails = form.getValues(type) || [];
    if (!currentEmails.includes(trimmedEmail)) {
      form.setValue(type, [...currentEmails, trimmedEmail], { shouldValidate: true });
    }

    if (type === 'to') setToInput('');
    else setCcInput('');
  };

  const removeEmail = (type: 'to' | 'cc', email: string) => {
    const currentEmails = form.getValues(type) || [];
    form.setValue(
      type,
      currentEmails.filter((e) => e !== email),
      { shouldValidate: true }
    );
  };

  const handleFormSubmit = async (data: SendEmailInput) => {
    try {
      // Appeler l'Edge Function pour envoyer l'email
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          to: data.to,
          cc: data.cc,
          subject: data.subject,
          body: data.body,
          entity_type: entityType,
          entity_id: entityId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erreur lors de l'envoi");
      }

      toast.success('Email envoyé', {
        description: `Email envoyé à ${data.to.join(', ')}`,
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email:", error);
      toast.error('Erreur', {
        description: error instanceof Error ? error.message : "Impossible d'envoyer l'email",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Envoyer un e-mail"
      size="2xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Envoyer"
          isSubmitting={form.formState.isSubmitting}
          formId="send-email-form"
        />
      }
    >
      <Form {...form}>
        <form id="send-email-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            {/* À */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                À <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addEmail('to', toInput);
                  }
                }}
                onBlur={() => {
                  if (toInput.trim()) {
                    addEmail('to', toInput);
                  }
                }}
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
              />
              {toEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {toEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 rounded-md text-sm"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail('to', email)}
                        className="hover:text-zinc-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {form.formState.errors.to && (
                <p className="text-sm text-red-600 mt-1">{form.formState.errors.to.message}</p>
              )}
            </div>

            {/* CC (optionnel) */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                CC <span className="text-zinc-400">(optionnel)</span>
              </label>
              <input
                type="email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addEmail('cc', ccInput);
                  }
                }}
                onBlur={() => {
                  if (ccInput.trim()) {
                    addEmail('cc', ccInput);
                  }
                }}
                placeholder="copie@example.com (optionnel)"
                className="w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
              />
              {ccEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ccEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 rounded-md text-sm"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail('cc', email)}
                        className="hover:text-zinc-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Sélecteur de modèle (V4 R8) */}
            <EmailTemplateSelector
              value={selectedTemplateId}
              templates={emailTemplates}
              onChange={handleApplyTemplate}
              selectId="send-email-template-select"
            />

            {/* Sujet */}
            <FormInput
              control={form.control as any}
              name="subject"
              label="Sujet"
              required
              placeholder="Objet de l'email"
            />

            {/* Message avec éditeur riche */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <Controller
                name="body"
                control={form.control}
                render={({ field, fieldState }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Rédigez votre message..."
                    minHeight="300px"
                    error={fieldState.error?.message}
                    tagCategories={getTagsByEntityType(
                      entityType as 'contact' | 'crew' | 'prospect'
                    )}
                  />
                )}
              />
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
